import React, { useState } from 'react';
import { HexColorPicker } from 'react-colorful';
import { Popover, IconButton, Box, TextField, Stack } from '@mui/material';
import colorThumb from '~/assets/images/icons/color-picker.png';

type Props = {
  color?: string;
  onChange: (color: string) => void;
};

function isValidHex(value: string): boolean {
  return /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(value);
}

function normalizeHex(value: string): string | null {
  if (!isValidHex(value)) return null;

  let hex = value.replace('#', '');

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((ch) => ch + ch)
      .join('');
  }

  return `#${hex.toLowerCase()}`;
}

const ColorPicker = ({ color = '', onChange }: Props) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (value) onChange(value.startsWith('#') ? value : `#${value}`);
    else onChange('');
  };

  return (
    <Stack
      sx={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 0.8,
      }}
    >
      <IconButton
        onClick={handleOpen}
        sx={{
          borderRadius: '4px',
          overflow: 'hidden',
          width: '32px',
          height: '32px',
          p: 0,
        }}
      >
        {isValidHex(color) ? (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              backgroundColor: color,
            }}
          />
        ) : (
          <img src={colorThumb} alt='' />
        )}
      </IconButton>

      <TextField
        label=''
        variant='outlined'
        size='small'
        value={color}
        onChange={handleHexChange}
        sx={{ width: '100%' }}
        inputProps={{
          maxLength: 7,
        }}
        onBlur={() => isValidHex(color) && onChange(normalizeHex(color))}
        error={color && !isValidHex(color)}
        helperText={color && !isValidHex(color) ? 'Invalid hex code' : ''}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            boxShadow: 'none',
            backgroundColor: 'transparent',
            borderRadius: 1.5,
            p: 1.5,
          },
        }}
      >
        <Stack spacing={1}>
          <HexColorPicker
            color={color}
            onChange={(newColor) => isValidHex(newColor) && onChange(newColor)}
          />
        </Stack>
      </Popover>
    </Stack>
  );
};

export { ColorPicker, isValidHex };
